
import React, { useState, useEffect } from "react";
import { Button, Table, Form } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiDeleteForever } from "@mdi/js";

const TranscodeVideo = ({ onUpdateHlsResolutions, onUpdateDashResolutions, onUpdateHlsAudioDescriptions, onUpdateDashAudioDescriptions, onUpdateEnableHls, onUpdateEnableDash, disableResolutionToggles = false, defaultSetting = false }) => {
  // Default data entries
  const defaultHlsData = [
    { resolution: "240p", width: 426, height: 240, codec: "H_264", rateControlMode: "QVBR", maxBitrate: 700000, audioCodec: "AAC", audioBitrate: 64000, codingMode: "CODING_MODE_2_0", sampleRate: 48000 },
    { resolution: "480p", width: 854, height: 480, codec: "H_264", rateControlMode: "QVBR", maxBitrate: 2000000, audioCodec: "AAC", audioBitrate: 96000, codingMode: "CODING_MODE_2_0", sampleRate: 48000 },
    { resolution: "540p", width: 960, height: 540, codec: "H_264", rateControlMode: "QVBR", maxBitrate: 3000000, audioCodec: "AAC", audioBitrate: 128000, codingMode: "CODING_MODE_2_0", sampleRate: 48000 },
    { resolution: "720p", width: 1280, height: 720, codec: "H_264", rateControlMode: "QVBR", maxBitrate: 4000000, audioCodec: "AAC", audioBitrate: 160000, codingMode: "CODING_MODE_2_0", sampleRate: 48000 },
  ];
  
  const defaultDashData = [
    { resolution: "240p", width: 426, height: 240, codec: "H_264", rateControlMode: "QVBR", maxBitrate: 700000, audioCodec: "AAC", audioBitrate: 64000, codingMode: "CODING_MODE_2_0", sampleRate: 48000 },
    { resolution: "480p", width: 854, height: 480, codec: "H_264", rateControlMode: "QVBR", maxBitrate: 2000000, audioCodec: "AAC", audioBitrate: 96000, codingMode: "CODING_MODE_2_0", sampleRate: 48000 },
    { resolution: "540p", width: 960, height: 540, codec: "H_264", rateControlMode: "QVBR", maxBitrate: 3000000, audioCodec: "AAC", audioBitrate: 128000, codingMode: "CODING_MODE_2_0", sampleRate: 48000 },
    { resolution: "720p", width: 1280, height: 720, codec: "H_264", rateControlMode: "QVBR", maxBitrate: 4000000, audioCodec: "AAC", audioBitrate: 160000, codingMode: "CODING_MODE_2_0", sampleRate: 48000 },
  ];

  const [enableHls, setEnableHls] = useState(true);
  const [enableDash, setEnableDash] = useState(true);
  const [hlsResolutions, setHlsResolutions] = useState(
    defaultSetting ? defaultHlsData.map(item => ({ resolution: item.resolution, codec: item.codec, height: item.height, width: item.width, bitrate: item.maxBitrate, maxBitrate: item.maxBitrate, rateControlMode: item.rateControlMode })) 
    : []
  );
  const [dashResolutions, setDashResolutions] = useState(
    defaultSetting ? defaultDashData.map(item => ({ resolution: item.resolution, codec: item.codec, height: item.height, width: item.width, bitrate: item.maxBitrate, maxBitrate: item.maxBitrate, rateControlMode: item.rateControlMode })) 
    : []
  );
  const [hlsAudioDescriptions, setHlsAudioDescriptions] = useState(
    defaultSetting ? defaultHlsData.map(item => ({ codec: item.audioCodec, bitrate: item.audioBitrate, codingMode: item.codingMode, sampleRate: item.sampleRate })) 
    : []
  );
  const [dashAudioDescriptions, setDashAudioDescriptions] = useState(
    defaultSetting ? defaultDashData.map(item => ({ codec: item.audioCodec, bitrate: item.audioBitrate, codingMode: item.codingMode, sampleRate: item.sampleRate })) 
    : []
  );

  // Update state when defaultSetting changes
  useEffect(() => {
    if (defaultSetting) {
      setHlsResolutions(defaultHlsData.map(item => ({ resolution: item.resolution, codec: item.codec, height: item.height, width: item.width, bitrate: item.maxBitrate, maxBitrate: item.maxBitrate, rateControlMode: item.rateControlMode })));
      setDashResolutions(defaultDashData.map(item => ({ resolution: item.resolution, codec: item.codec, height: item.height, width: item.width, bitrate: item.maxBitrate, maxBitrate: item.maxBitrate, rateControlMode: item.rateControlMode })));
      setHlsAudioDescriptions(defaultHlsData.map(item => ({ codec: item.audioCodec, bitrate: item.audioBitrate, codingMode: item.codingMode, sampleRate: item.sampleRate })));
      setDashAudioDescriptions(defaultDashData.map(item => ({ codec: item.audioCodec, bitrate: item.audioBitrate, codingMode: item.codingMode, sampleRate: item.sampleRate })));
      // Enable both HLS and DASH when default setting is enabled
      setEnableHls(true);
      setEnableDash(true);
    } else {
      setHlsResolutions([]);
      setDashResolutions([]);
      setHlsAudioDescriptions([]);
      setDashAudioDescriptions([]);
      // Enable both HLS and DASH when custom setting is enabled
      setEnableHls(true);
      setEnableDash(true);
    }
  }, [defaultSetting]);

  useEffect(() => {
    if (onUpdateHlsResolutions) {
      const filteredHls = hlsResolutions.filter(res => res.resolution && res.codec);
      onUpdateHlsResolutions(enableHls ? filteredHls : []);
    }
  }, [hlsResolutions, enableHls]);

  // Effect to notify parent about DASH resolutions
  useEffect(() => {
    if (onUpdateDashResolutions) {
      const filteredDash = dashResolutions.filter(res => res.resolution && res.codec);
      onUpdateDashResolutions(enableDash ? filteredDash : []);
    }
  }, [dashResolutions, enableDash]);

  // Effect to notify parent about HLS audio descriptions
  useEffect(() => {
    if (onUpdateHlsAudioDescriptions) {
      const filteredHls = hlsResolutions.filter(res => res.resolution && res.codec);
      const audioDescriptions = enableHls && filteredHls.length > 0 ? hlsAudioDescriptions.slice(0, filteredHls.length) : [];
      onUpdateHlsAudioDescriptions(audioDescriptions);
    }
  }, [hlsAudioDescriptions, hlsResolutions, enableHls]);

  // Effect to notify parent about DASH audio descriptions
  useEffect(() => {
    if (onUpdateDashAudioDescriptions) {
      const filteredDash = dashResolutions.filter(res => res.resolution && res.codec);
      const audioDescriptions = enableDash && filteredDash.length > 0 ? dashAudioDescriptions.slice(0, filteredDash.length) : [];
      onUpdateDashAudioDescriptions(audioDescriptions);
    }
  }, [dashAudioDescriptions, dashResolutions, enableDash]);

  // Effect to notify parent about HLS and DASH enable states
  useEffect(() => {
    if (onUpdateEnableHls) {
      onUpdateEnableHls(enableHls);
    }
  }, [enableHls]);

  useEffect(() => {
    if (onUpdateEnableDash) {
      onUpdateEnableDash(enableDash);
    }
  }, [enableDash]);

  const handleAddResolution = (group) => {
    const newRow = { resolution: "", codec: "", height: "", width: "", bitrate: "", rateControlMode: "" };
    const newAudioRow = { codec: "", bitrate: "", codingMode: "CODING_MODE_2_0", sampleRate: "48000" };
    if (group === "HLS") {
      setHlsResolutions(prev => [...prev, newRow]);
      setHlsAudioDescriptions(prev => [...prev, newAudioRow]);
    } else {
      setDashResolutions(prev => [...prev, newRow]);
      setDashAudioDescriptions(prev => [...prev, newAudioRow]);
    }
  };

  const handleDeleteResolution = (group, index) => {
    if (group === "HLS") {
      const updatedRows = hlsResolutions.filter((_, i) => i !== index);
      const updatedAudioRows = hlsAudioDescriptions.filter((_, i) => i !== index);
      setHlsResolutions(updatedRows);
      setHlsAudioDescriptions(updatedAudioRows);
    } else {
      const updatedRows = dashResolutions.filter((_, i) => i !== index);
      const updatedAudioRows = dashAudioDescriptions.filter((_, i) => i !== index);
      setDashResolutions(updatedRows);
      setDashAudioDescriptions(updatedAudioRows);
    }
  };
  const resolutionPresets = {
    "240p": { width: 426, height: 240, maxBitrate: 700000 },
    "360p": { width: 640, height: 360, maxBitrate: 1000000 },
    "480p": { width: 854, height: 480, maxBitrate: 2000000 },
    "540p": { width: 960, height: 540, maxBitrate: 3000000 },
    "720p": { width: 1280, height: 720, maxBitrate: 4000000 },
    "1080p": { width: 1920, height: 1080, maxBitrate: 8000000 }
  };

  const handleInputChange = (group, index, field, value) => {
    const updateState = (rows, setter) => {
      const updatedRows = [...rows];
      if (field === "resolution" && resolutionPresets[value]) {
        updatedRows[index] = {
          ...updatedRows[index],
          resolution: value,
          width: resolutionPresets[value].width,
          height: resolutionPresets[value].height,
          bitrate: resolutionPresets[value].maxBitrate,
          maxBitrate: resolutionPresets[value].maxBitrate
        };
      } else {
        updatedRows[index][field] = value;
      }
      setter(updatedRows);
    };

    if (group === "HLS") {
      updateState(hlsResolutions, setHlsResolutions);
    } else {
      updateState(dashResolutions, setDashResolutions);
    }
  };

  const handleAudioInputChange = (group, index, field, value) => {
    if (group === "HLS") {
      const updatedRows = [...hlsAudioDescriptions];
      updatedRows[index][field] = value;
      setHlsAudioDescriptions(updatedRows);
    } else {
      const updatedRows = [...dashAudioDescriptions];
      updatedRows[index][field] = value;
      setDashAudioDescriptions(updatedRows);
    }
  };

  const renderRows = (rows, group) =>
    rows.map((row, index) => {
      const maxBitrate = row.maxBitrate || row.bitrate;
      const bitrateValue = row.bitrate || row.maxBitrate || "";
      const audioRow = group === "HLS" ? hlsAudioDescriptions[index] || {} : dashAudioDescriptions[index] || {};
      
      // Determine which bitrate value to show based on RateControlMode
      const isCBR = row.rateControlMode === "CBR";
      const displayBitrate = isCBR ? bitrateValue : maxBitrate;
      
      // Get all available resolutions
      const allResolutions = ["240p", "360p", "480p", "540p", "720p", "1080p"];
      
      // Get resolutions already selected in other rows of the same group (excluding current row)
      const selectedResolutions = rows
        .map((r, i) => i !== index ? r.resolution : null)
        .filter(r => r && r !== "");
      
      // Filter available resolutions: show current selection + unselected ones
      const availableResolutions = allResolutions.filter(res => 
        res === row.resolution || !selectedResolutions.includes(res)
      );
      
      return (
        <tr key={index} className="border-bottom">
          {/* Video Resolution Columns */}
          <td>
            {defaultSetting ? (
              row.resolution
            ) : (
              <Form.Select
                value={row.resolution}
                onChange={(e) => handleInputChange(group, index, "resolution", e.target.value)}
              >
                <option value="">Select</option>
                {availableResolutions.map(res => (
                  <option key={res} value={res}>{res}</option>
                ))}
              </Form.Select>
            )}
          </td>
          <td>
            {defaultSetting ? (
              row.codec === "H_264" ? "H.264" : row.codec
            ) : (
              <Form.Select
                value={row.codec}
                onChange={(e) => handleInputChange(group, index, "codec", e.target.value)}
              >
                <option value="">Select</option>
                <option value="H_264">H.264</option>
              </Form.Select>
            )}
          </td>
          <td>
            <Form.Control
              type="number"
              value={row.height}
              readOnly
            />
          </td>
          <td>
            <Form.Control
              type="number"
              value={row.width}
              readOnly
            />
          </td>
          <td>
            {defaultSetting ? (
              row.rateControlMode
            ) : (
              <Form.Select
                value={row.rateControlMode}
                onChange={(e) => {
                  const newMode = e.target.value;
                  handleInputChange(group, index, "rateControlMode", newMode);
                  // When switching modes, preserve the bitrate value
                  if (newMode === "CBR") {
                    handleInputChange(group, index, "bitrate", row.maxBitrate || row.bitrate || "");
                  } else {
                    handleInputChange(group, index, "maxBitrate", row.bitrate || row.maxBitrate || "");
                  }
                }}
              >
                <option value="">Select</option>
                <option value="QVBR">QVBR</option>
                <option value="CBR">CBR</option>
              </Form.Select>
            )}
          </td>
          {!defaultSetting && (
            <td style={{ borderRight: '2px solid #dee2e6' }}>
              <Form.Control
                type="number"
                value={displayBitrate}
                onChange={(e) => {
                  const value = e.target.value;
                  // Update both bitrate and maxBitrate, the payload will use the correct one
                  handleInputChange(group, index, "bitrate", value);
                  handleInputChange(group, index, "maxBitrate", value);
                }}
                placeholder={isCBR ? "Bitrate" : "Max Bitrate"}
              />
            </td>
          )}
          {defaultSetting && (
            <td style={{ borderRight: '2px solid #dee2e6' }}>{maxBitrate}</td>
          )}
          
          {/* Audio Description Columns */}
          <td style={{ paddingLeft: '15px' }}>
            {defaultSetting ? (
              audioRow.codec || ''
            ) : (
              <Form.Select
                value={audioRow.codec || ''}
                onChange={(e) => handleAudioInputChange(group, index, "codec", e.target.value)}
              >
                <option value="">Select</option>
                <option value="AAC">AAC</option>
              </Form.Select>
            )}
          </td>
          <td>
            {defaultSetting ? (
              audioRow.bitrate || ''
            ) : (
              <Form.Control
                type="number"
                value={audioRow.bitrate || ''}
                onChange={(e) => handleAudioInputChange(group, index, "bitrate", e.target.value)}
                placeholder="Enter bitrate"
              />
            )}
          </td>
          <td>
            {defaultSetting ? (
              audioRow.sampleRate || ''
            ) : (
              <Form.Select
                value={audioRow.sampleRate || '48000'}
                onChange={(e) => handleAudioInputChange(group, index, "sampleRate", e.target.value)}
              >
                <option value="48000">48000 Hz</option>
              </Form.Select>
            )}
          </td>
          
          {/* Action Column */}
          {!defaultSetting && (
            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
              <Button
                variant="link"
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  color: "red",
                  padding: "0",
                  minWidth: "auto"
                }}
                onClick={() => handleDeleteResolution(group, index)}
              >
                <Icon path={mdiDeleteForever} size={1.5} />
              </Button>
            </td>
          )}
        </tr>
      );
    });

  const renderAudioRows = (audioRows, group) =>
    audioRows.map((row, index) => (
      <tr key={index} className="border-bottom">
        <td>
          {defaultSetting ? (
            row.codec
          ) : (
            <Form.Select
              value={row.codec}
              onChange={(e) => handleAudioInputChange(group, index, "codec", e.target.value)}
            >
              <option value="">Select</option>
              <option value="AAC">AAC</option>
            </Form.Select>
          )}
        </td>
        <td>
          {defaultSetting ? (
            row.bitrate
          ) : (
            <Form.Control
              type="number"
              value={row.bitrate}
              onChange={(e) => handleAudioInputChange(group, index, "bitrate", e.target.value)}
              placeholder="Enter bitrate"
            />
          )}
        </td>
        <td>
          {defaultSetting ? (
            row.sampleRate
          ) : (
            <Form.Select
              value={row.sampleRate}
              onChange={(e) => handleAudioInputChange(group, index, "sampleRate", e.target.value)}
            >
              <option value="">Select</option>
              <option value="44100">44100 Hz</option>
              <option value="48000">48000 Hz</option>
              <option value="96000">96000 Hz</option>
            </Form.Select>
          )}
        </td>
        {!defaultSetting && (
          <td>
            <Button
              style={{
                backgroundColor: "transparent",
                border: "none",
                color: "red",
              }}
              onClick={() => handleDeleteResolution(group, index)}
            >
              <Icon path={mdiDeleteForever} size={1.5} />
            </Button>
          </td>
        )}
      </tr>
    ));

  return (
    <div className="container-fluid px-0">
      {/* HLS Group */}
      <div>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0" style={{ marginLeft: "10px" }}>HLS Group</h5>
          <Form.Check
            type="switch"
            id="enable-hls"
            label="Enable HLS"
            checked={enableHls}
            onChange={() => setEnableHls(!enableHls)}
            className="me-2"
            disabled={disableResolutionToggles}
          />
          <Button
            style={{ background: "#eef9fd", color: "#50c3e6", border: "0", marginRight: "10px" }}
            onClick={() => handleAddResolution("HLS")}
            disabled={!enableHls || disableResolutionToggles}
          >
            Add Resolution
          </Button>
        </div>
        <div className="table-responsive">
        <Table striped hover className="align-middle mb-0 mt-3" style={{ width: '100%', tableLayout: 'auto' }}>
          <thead className="text-muted table-light">
            <tr>
              <th colSpan={ 6 } style={{ textAlign: 'center', backgroundColor: '#f8f9fa', borderRight: '2px solid #dee2e6' }}>
                Video Description
              </th>
              <th colSpan={4} style={{ textAlign: 'center', backgroundColor: '#e9ecef' }}>
                Audio Description
              </th>
            </tr>
            <tr>
              <th scope="col">Resolution</th>
              <th scope="col">Video Codec</th>
              <th scope="col">Height</th>
              <th scope="col">Width</th>
              <th scope="col">RateControlMode</th>
              {!defaultSetting && (
                <th scope="col" style={{ borderRight: '2px solid #dee2e6' }}>MaxBitrate / Bitrate</th>
              )}
              {defaultSetting && (
                <th scope="col" style={{ borderRight: '2px solid #dee2e6' }}>MaxBitrate</th>
              )}
              <th scope="col" style={{ paddingLeft: '15px' }}>Codec</th>
              <th scope="col">Bitrate</th>
              <th scope="col">SampleRate</th>
              {!defaultSetting && <th scope="col" style={{ minWidth: '60px' }}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {enableHls ? (
              defaultSetting ? (
                hlsResolutions.length > 0 ? (
                  renderRows(hlsResolutions, "HLS")
                ) : null
              ) : !disableResolutionToggles ? (
                hlsResolutions.length > 0 ? (
                  renderRows(hlsResolutions, "HLS")
                ) : (
                  <tr>
                    <td colSpan={defaultSetting ? 9 : 10} className="text-center text-muted">
                      No resolutions added. Click "Add Resolution" to add one.
                    </td>
                  </tr>
                )
              ) : null
            ) : null}
          </tbody>
        </Table>
        </div>
      </div>

      {/* DASH Group */}
      <div className="mt-5 mb-4">
        <hr />
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 " style={{ marginLeft: "10px" }}>DASH Group</h5>
          <Form.Check
            type="switch"
            id="enable-dash"
            label="Enable DASH"
            checked={enableDash}
            onChange={() => setEnableDash(!enableDash)}
            className="me-2"
            disabled={disableResolutionToggles || defaultSetting}
          />
          <Button
            style={{ background: "#eef9fd", color: "#50c3e6", border: "0", marginRight: "10px" }}
            onClick={() => handleAddResolution("DASH")}
            disabled={!enableDash || disableResolutionToggles}
          >
            Add Resolution
          </Button>
        </div>
        <div className="table-responsive">
        <Table striped hover className="align-middle mb-0 mt-3" style={{ width: '100%', tableLayout: 'auto' }}>
          <thead className="text-muted table-light">
            <tr>
              <th colSpan={ 6 } style={{ textAlign: 'center', backgroundColor: '#f8f9fa', borderRight: '2px solid #dee2e6' }}>
                Video Description
              </th>
              <th colSpan={4} style={{ textAlign: 'center', backgroundColor: '#e9ecef' }}>
                Audio Description
              </th>
            </tr>
            <tr>
              <th scope="col">Resolution</th>
              <th scope="col">Video Codec</th>
              <th scope="col">Height</th>
              <th scope="col">Width</th>
              <th scope="col">RateControlMode</th>
              {!defaultSetting && (
                <th scope="col" style={{ borderRight: '2px solid #dee2e6' }}>MaxBitrate / Bitrate</th>
              )}
              {defaultSetting && (
                <th scope="col" style={{ borderRight: '2px solid #dee2e6' }}>MaxBitrate</th>
              )}
              <th scope="col" style={{ paddingLeft: '15px' }}>Codec</th>
              <th scope="col">Bitrate</th>
              <th scope="col">SampleRate</th>
              {!defaultSetting && <th scope="col" style={{ minWidth: '60px' }}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {enableDash ? (
              defaultSetting ? (
                dashResolutions.length > 0 ? (
                  renderRows(dashResolutions, "DASH")
                ) : null
              ) : !disableResolutionToggles ? (
                dashResolutions.length > 0 ? (
                  renderRows(dashResolutions, "DASH")
                ) : (
                  <tr>
                    <td colSpan={defaultSetting ? 9 : 10} className="text-center text-muted">
                      No resolutions added. Click "Add Resolution" to add one.
                    </td>
                  </tr>
                )
              ) : null
            ) : null}
          </tbody>
        </Table>
        </div>
      </div>
    </div>
  );
};

export default TranscodeVideo;
